import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../services/authService';
import Button from '../components/Button';
import { US_STATES } from '../data/curriculumStandards';
import { UserRole } from '../types';

const NewRegisterPage: React.FC = () => {
    const navigate = useNavigate();

    // Account Type Selection
    const [accountType, setAccountType] = useState<'student' | 'parent' | 'teacher' | null>(null);

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        grade: 4,
        state: 'TX'
    });

    const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!agreedToPrivacy) {
            setError("You must agree to the Privacy Policy to continue.");
            return;
        }

        if (!accountType) {
            setError("Please select an account type.");
            return;
        }

        setLoading(true);

        try {
            const result = await authService.register({
                name: formData.name,
                email: formData.email,
                password: formData.password,
                grade: accountType === 'student' ? formData.grade : 0, // Parents/Teachers don't have a specific grade level on profile
                state: formData.state,
                role: accountType === 'parent' ? UserRole.PARENT : (accountType === 'teacher' ? UserRole.TEACHER : UserRole.STUDENT)
            });

            if (result.success) {
                navigate('/dashboard');
            } else {
                setError(result.message || 'Registration failed');
            }
        } catch (err) {
            setError('An error occurred during registration.');
        } finally {
            setLoading(false);
        }
    };

    // Step 1: Account Type Selection
    if (!accountType) {
        return (
            <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
                <div className="bg-white p-8 rounded-3xl shadow-xl w-full max-w-2xl border border-slate-100">
                    <div className="text-center mb-8">
                        <img src="/logo.png" alt="CleverCubby" className="h-24 w-24 mx-auto mb-4 object-contain" />
                        <h1 className="text-3xl font-bold text-indigo-900 brand-font mb-2">
                            Welcome to CleverCubby!
                        </h1>
                        <p className="text-slate-500">Choose your account type to get started</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                        {/* Student Account */}
                        <button
                            onClick={() => setAccountType('student')}
                            className="group p-6 border-2 border-slate-200 rounded-3xl hover:border-brand-blue hover:shadow-xl transition-all text-left"
                        >
                            <div className="text-4xl mb-4">🎓</div>
                            <h3 className="text-xl font-bold text-slate-800 mb-2 group-hover:text-brand-blue transition-colors">
                                Student
                            </h3>
                            <p className="text-slate-600 text-xs mb-4">
                                Practice quizzes, earn points, and track your progress.
                            </p>
                            <div className="flex items-center text-brand-blue font-bold text-xs">
                                Get Started <i className="fas fa-arrow-right ml-2"></i>
                            </div>
                        </button>

                        {/* Parent Account */}
                        <button
                            onClick={() => setAccountType('parent')}
                            className="group p-6 border-2 border-slate-200 rounded-3xl hover:border-purple-500 hover:shadow-xl transition-all text-left"
                        >
                            <div className="text-4xl mb-4">👨‍👩‍👧‍👦</div>
                            <h3 className="text-xl font-bold text-slate-800 mb-2 group-hover:text-purple-600 transition-colors">
                                Parent
                            </h3>
                            <p className="text-slate-600 text-xs mb-4">
                                Manage your children's learning and monitor progress.
                            </p>
                            <div className="flex items-center text-purple-600 font-bold text-xs">
                                Get Started <i className="fas fa-arrow-right ml-2"></i>
                            </div>
                        </button>

                        {/* Teacher Account */}
                        <button
                            onClick={() => setAccountType('teacher')}
                            className="group p-6 border-2 border-slate-200 rounded-3xl hover:border-teal-500 hover:shadow-xl transition-all text-left"
                        >
                            <div className="text-4xl mb-4">🍎</div>
                            <h3 className="text-xl font-bold text-slate-800 mb-2 group-hover:text-teal-600 transition-colors">
                                Teacher
                            </h3>
                            <p className="text-slate-600 text-xs mb-4">
                                Create classes, assign quizzes, and track class analytics.
                            </p>
                            <div className="flex items-center text-teal-600 font-bold text-xs">
                                Get Started <i className="fas fa-arrow-right ml-2"></i>
                            </div>
                        </button>
                    </div>

                    <div className="mt-8 text-center text-sm text-slate-500">
                        Already have an account? <Link to="/login" className="text-brand-blue font-bold hover:underline">Log in</Link>
                    </div>
                </div>
            </div>
        );
    }

    // Step 2: Registration Form
    return (
        <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
            <div className="bg-white p-8 rounded-3xl shadow-xl w-full max-w-md border border-slate-100">
                <button
                    onClick={() => setAccountType(null)}
                    className="text-slate-400 hover:text-slate-600 mb-4 flex items-center text-sm"
                >
                    <i className="fas fa-arrow-left mr-2"></i> Back
                </button>

                <div className="text-center mb-8">
                    <span className="text-4xl mb-4 block">
                        {accountType === 'parent' && '👨‍👩‍👧‍👦'}
                        {accountType === 'student' && '🎓'}
                        {accountType === 'teacher' && '🍎'}
                    </span>
                    <h1 className="text-2xl font-bold text-indigo-900 brand-font">
                        {accountType === 'parent' && 'Create Parent Account'}
                        {accountType === 'student' && 'Create Student Account'}
                        {accountType === 'teacher' && 'Create Teacher Account'}
                    </h1>
                    <p className="text-slate-500">
                        {accountType === 'parent' && 'Manage your children\'s learning journey'}
                        {accountType === 'student' && 'Start your learning adventure'}
                        {accountType === 'teacher' && 'Access tools to manage your classroom'}
                    </p>
                </div>

                {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm mb-4 text-center font-bold border border-red-100">
                        <i className="fas fa-exclamation-circle mr-1"></i> {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">
                            {accountType === 'parent' ? 'Your Name' : 'Student Name'}
                        </label>
                        <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-blue outline-none"
                            placeholder="e.g. Alex Johnson"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">Email Address</label>
                        <input
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-blue outline-none"
                            placeholder="email@example.com"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">Password</label>
                        <input
                            type="password"
                            required
                            minLength={6}
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-blue outline-none"
                            placeholder="Create a secure password"
                        />
                    </div>

                    {accountType === 'student' && (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Grade</label>
                                <select
                                    value={formData.grade}
                                    onChange={(e) => setFormData({ ...formData, grade: Number(e.target.value) })}
                                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-blue outline-none bg-white"
                                >
                                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(g => <option key={g} value={g}>{g === 0 ? 'K' : `Grade ${g}`}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">State</label>
                                <select
                                    value={formData.state}
                                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-blue outline-none bg-white"
                                >
                                    {US_STATES.map(state => (
                                        <option key={state.code} value={state.code}>{state.code}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    {accountType === 'parent' && (
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-1">State</label>
                            <select
                                value={formData.state}
                                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-blue outline-none bg-white"
                            >
                                {US_STATES.map(state => (
                                    <option key={state.code} value={state.code}>{state.name} ({state.code})</option>
                                ))}
                            </select>
                            <p className="text-xs text-slate-400 mt-1">
                                <i className="fas fa-info-circle mr-1"></i>
                                You can add your children after creating your account
                            </p>
                        </div>
                    )}

                    {accountType === 'teacher' && (
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-1">State (Curriculum)</label>
                            <select
                                value={formData.state}
                                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-blue outline-none bg-white"
                            >
                                {US_STATES.map(state => (
                                    <option key={state.code} value={state.code}>{state.name} ({state.code})</option>
                                ))}
                            </select>
                            <p className="text-xs text-slate-400 mt-1">
                                <i className="fas fa-info-circle mr-1"></i>
                                Classes will default to this state's curriculum standards
                            </p>
                        </div>
                    )}

                    <div className="flex items-start gap-3 mt-4 p-3 bg-slate-50 rounded-xl border border-slate-100">
                        <input
                            type="checkbox"
                            id="privacy"
                            checked={agreedToPrivacy}
                            onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                            className="mt-1 w-5 h-5 text-brand-blue rounded focus:ring-brand-blue cursor-pointer"
                        />
                        <label htmlFor="privacy" className="text-sm text-slate-600 cursor-pointer select-none">
                            I agree to the <a href="#" className="text-brand-blue hover:underline">Terms of Service</a> and <a href="#" className="text-brand-blue hover:underline">Privacy Policy</a>.
                        </label>
                    </div>

                    <Button type="submit" disabled={loading} className="w-full justify-center py-3 mt-4">
                        {loading ? 'Creating Account...' : 'Create Account'}
                    </Button>
                </form>

                <div className="mt-6 text-center text-sm text-slate-500">
                    Already have an account? <Link to="/login" className="text-brand-blue font-bold hover:underline">Log in</Link>
                </div>
            </div>
        </div>
    );
};

export default NewRegisterPage;
